/**
 * Model Exporter
 * Main module for exporting 3D models to various formats
 * Provides unified interface for STL and PLY export
 */

import { meshToSTL, createSTLBlob } from './stlExporter.js';
import { meshToPLY, createPLYBlob } from './plyExporter.js';
import { EXPORT_FORMATS, generateFileName } from '../exportConfig.js';

/**
 * Export Three.js mesh to specified format
 * @param {THREE.Mesh} mesh - The mesh to export
 * @param {Object} options - Export options
 *   - format: 'stl' or 'ply' (default: 'stl')
 *   - filename: custom filename (default: auto-generated)
 *   - preserveColors: whether to preserve vertex colors (default: true)
 * @returns {Blob} Exported data as Blob
 */
export function exportMesh(mesh, options = {}) {
  const {
    format = EXPORT_FORMATS.STL,
    filename,
    preserveColors = true
  } = options;

  if (!mesh || !mesh.geometry) {
    throw new Error('Invalid mesh provided for export');
  }

  let data = null;
  let blob = null;

  try {
    switch (format) {
      case EXPORT_FORMATS.STL:
        data = meshToSTL(mesh);
        blob = createSTLBlob(data);
        break;

      case EXPORT_FORMATS.PLY:
        data = meshToPLY(mesh, { preserveColors });
        blob = createPLYBlob(data);
        break;

      default:
        throw new Error(`Unsupported export format: ${format}`);
    }

    return blob;
  } catch (error) {
    console.error(`Error exporting mesh to ${format.toUpperCase()}:`, error);
    throw error;
  }
}

/**
 * Export mesh and trigger download
 * @param {THREE.Mesh} mesh - The mesh to export
 * @param {Object} options - Export options
 *   - format: 'stl' or 'ply'
 *   - filename: custom filename
 *   - preserveColors: whether to preserve colors
 */
export function downloadMesh(mesh, options = {}) {
  const {
    format = EXPORT_FORMATS.STL,
    filename = generateFileName('socket', format),
    preserveColors = true
  } = options;

  try {
    const blob = exportMesh(mesh, { format, preserveColors });
    downloadBlob(blob, filename);
  } catch (error) {
    console.error('Error downloading mesh:', error);
    throw error;
  }
}

/**
 * Export mesh and return as data URL (useful for previews or further processing)
 * @param {THREE.Mesh} mesh - The mesh to export
 * @param {Object} options - Export options
 * @returns {string} Data URL
 */
export function exportMeshAsDataURL(mesh, options = {}) {
  try {
    const blob = exportMesh(mesh, options);
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error creating data URL:', error);
    throw error;
  }
}

/**
 * Helper function to trigger file download
 * @param {Blob} blob - Data blob
 * @param {string} filename - Filename
 */
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Get supported export formats
 * @returns {Object} Map of format names to format values
 */
export function getSupportedFormats() {
  return EXPORT_FORMATS;
}
